import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert, Button, Grid, MenuItem, OutlinedInput, Select, Stack,
} from '@mui/material';

import { applyApiFieldErrors } from '../../../shared/api';
import LabeledField from '../../../shared/components/LabeledField';
import { resolveUserFacingError } from '../../../shared/utils/actionFeedback';
import {
  toElectricitySupplierForm,
  toElectricitySupplierPayload,
} from '../model/electricitySupplierForm';

export default function ElectricitySupplierFormPanel({
  supplier, saving, onCancel, onSubmit,
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(supplier);
  const {
    control, handleSubmit, reset, setError, formState: { errors, isDirty },
  } = useForm({ mode: 'onTouched', defaultValues: toElectricitySupplierForm(supplier) });

  useEffect(() => reset(toElectricitySupplierForm(supplier)), [reset, supplier]);
  const requiredRule = { required: t('common.fieldRequired') };
  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(toElectricitySupplierPayload(values, { isEdit }));
    } catch (error) {
      if (applyApiFieldErrors(error, setError, t)) return;
      setError('root.server', {
        message: resolveUserFacingError(
          error,
          t('office.supplierCatalog.saveFailed'),
        ),
      });
    }
  });

  return (
    <Grid
      container
      spacing={2}
      component="form"
      id="electricity-supplier-form"
      onSubmit={submit}
    >
      {errors.root?.server?.message && (
        <Grid item xs={12}>
          <Alert severity="error">{errors.root.server.message}</Alert>
        </Grid>
      )}
      <Grid item xs={12} md={6}>
        <Controller
          name="registration_no"
          control={control}
          rules={{
            ...requiredRule,
            pattern: {
              value: /^[A-Za-z0-9-]+$/,
              message: t('office.supplierCatalog.registrationInvalid'),
            },
          }}
          render={({ field }) => (
            <LabeledField
              label={t('office.supplierCatalog.registrationNo')}
              htmlFor="supplier_registration_no"
              required
              error={errors.registration_no?.message}
            >
              <OutlinedInput
                {...field}
                id="supplier_registration_no"
                size="small"
                placeholder="A0270"
              />
            </LabeledField>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Controller
          name="supplier_name_ja"
          control={control}
          rules={requiredRule}
          render={({ field }) => (
            <LabeledField
              label={t('office.supplierCatalog.name')}
              htmlFor="supplier_name_ja"
              required
              error={errors.supplier_name_ja?.message}
            >
              <OutlinedInput {...field} id="supplier_name_ja" size="small" />
            </LabeledField>
          )}
        />
      </Grid>
      {isEdit && (
        <Grid item xs={12} md={6}>
          <Controller
            name="is_active"
            control={control}
            render={({ field }) => (
              <LabeledField label={t('common.status')} htmlFor="supplier_is_active">
                <Select {...field} id="supplier_is_active" size="small">
                  <MenuItem value={true}>{t('common.active')}</MenuItem>
                  <MenuItem value={false}>{t('common.inactive')}</MenuItem>
                </Select>
              </LabeledField>
            )}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={onCancel} disabled={saving}>{t('common.cancel')}</Button>
          <Button type="submit" variant="contained" disabled={saving || (isEdit && !isDirty)}>
            {t(isEdit ? 'common.update' : 'common.create')}
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}
